/**
 * Asset URLs (Cloudflare R2)
 * Base URL for app assets served from R2; object key = path after domain.
 */

import { Platform, type ImageSourcePropType } from 'react-native';
import type { AnimationObject } from 'lottie-react-native';
import { recolorHandTapLottieToWhite } from '../src/create/helpers/handTapLottie.helper';
import { logger } from '../src/helpers/logger.helper';
import { cdnObjectKeyPreferWebp } from './cdnObjectKey.helper';
import { AVATAR_IDS, type AvatarId } from './avatarIds';

/** R2 public hostname — must not be the Vercel app host (apex/www). */
const DEFAULT_ASSETS_BASE_URL = 'https://assets.zporti.com';

/** App hosts that must not serve R2 paths (would 404 on Vercel after DNS fix). */
const LEGACY_ASSET_BASE_HOSTS = new Set(['zporti.com', 'www.zporti.com']);

const normalizeAssetsBaseUrl = (rawUrl?: string): string => {
  const trimAndFixProtocol = (s: string): string => {
    const t = s.trim();
    return t.startsWith('https:www.')
      ? t.replace('https:www.', 'https://www.')
      : t;
  };

  const apply = (urlString: string): string => {
    const parsedUrl = new URL(urlString);
    const host = LEGACY_ASSET_BASE_HOSTS.has(parsedUrl.hostname)
      ? new URL(DEFAULT_ASSETS_BASE_URL).hostname
      : parsedUrl.hostname;
    return `${parsedUrl.protocol}//${host}`.replace(/\/$/, '');
  };

  if (!rawUrl) return DEFAULT_ASSETS_BASE_URL;

  try {
    return apply(trimAndFixProtocol(rawUrl));
  } catch {
    logger.warn(
      `[CDN] Invalid EXPO_PUBLIC_ASSETS_BASE_URL "${rawUrl}", using default ${DEFAULT_ASSETS_BASE_URL}`
    );
    return DEFAULT_ASSETS_BASE_URL;
  }
};

export const ASSETS_BASE_URL = normalizeAssetsBaseUrl(
  process.env.EXPO_PUBLIC_ASSETS_BASE_URL
);

/**
 * Build full URL for an R2 asset by path (object key).
 * Legacy `.png` keys are rewritten to `.webp` (CDN objects are webp-only).
 * @param path - Object key, e.g. 'assets/TutorialPlaceHolderPhoto1.webp'
 * @returns Full URL, e.g. 'https://assets.zporti.com/assets/TutorialPlaceHolderPhoto1.webp'
 */
export const getAssetUrl = (path: string): string => {
  const stripped = path.startsWith('/') ? path.slice(1) : path;
  const normalized = cdnObjectKeyPreferWebp(stripped);
  return `${ASSETS_BASE_URL}/${normalized}`;
};

/**
 * CDN URL using the exact object key (no `.png` → `.webp` rewrite).
 * Use when the R2 object is PNG/JPEG-only (e.g. tutorial full-screen art).
 */
export const getCdnUrlExactKey = (objectKey: string): string => {
  const stripped = objectKey.startsWith('/') ? objectKey.slice(1) : objectKey;
  return `${ASSETS_BASE_URL}/${stripped}`;
};

/**
 * Bundled fallback sources — shipped in the app binary so these renders never depend on CDN reachability.
 * Group default cover uses CDN (ASSET_PATHS.DEFAULT_GROUP_IMAGE); see DEFAULT_GROUP_IMAGE_SOURCE below.
 */
export const PLAYER_SILHOUETTE_SOURCE: ImageSourcePropType =
  require('../assets/man.png');

/** Bundled Lottie JSON sources (lottie-react-native consumes JSON directly). */
export const VS_LOTTIE_SOURCE = require('../assets/Lottie/vs.json') as AnimationObject;
export const WINNER_LOTTIE_SOURCE = require('../assets/Lottie/winner.json') as AnimationObject;

const HAND_TAP_LOTTIE_BASE = require('../assets/Lottie/Hand_tap.json') as AnimationObject;
/** White hand for walkthrough dim overlay — avoids unreliable iOS Lottie colorFilters. */
export const HAND_TAP_LOTTIE_SOURCE = recolorHandTapLottieToWhite(
  HAND_TAP_LOTTIE_BASE,
) as AnimationObject;

/** Tutorial background (full-screen onboarding art) — bundled so the first-run tutorial paints instantly. */
export const TUTORIAL_BACKGROUND_SOURCE: ImageSourcePropType =
  require('../assets/Tutorial/Tutorial_Background_dark.png');

/**
 * Official app-store badges for the landing hero (web-only marketing surface).
 * Bundled (not CDN) so they ship with the web build, get content-hashed (no cache
 * issues), and can never 404 — fit for tiny static chrome that never changes.
 * Both are the unmodified official artwork, used at equal height and linked via
 * APP_STORE_URL / GOOGLE_PLAY_URL:
 *   • App Store: "Download on the App Store", black US/UK lockup (SVG → PNG, 431×144)
 *   • Google Play: "Get it on Google Play", English color (478×142)
 */
export const APP_STORE_BADGE_SOURCE: ImageSourcePropType =
  require('../assets/Landing/badge-app-store.png');
export const GOOGLE_PLAY_BADGE_SOURCE: ImageSourcePropType =
  require('../assets/Landing/badge-google-play.png');

/** Monkey "happy" 150 — bundled only for the Arena empty-state, where it's used as the no-content mascot. */
export const ARENA_EMPTY_MONKEY_SOURCE: ImageSourcePropType =
  require('../assets/Avatars/Monkey/Win-150.webp');

/**
 * Build full URL for the 150×150 variant of an R2 asset.
 * Inserts `-150` before the `.webp` extension.
 * @param path - Object key, e.g. 'assets/Avatars/BlueWithHair/idle.webp'
 * @returns Full URL, e.g. 'https://assets.zporti.com/assets/Avatars/BlueWithHair/idle-150.webp'
 */
export const getSmallAssetUrl = (path: string): string => {
  const stripped = path.startsWith('/') ? path.slice(1) : path;
  const normalized = cdnObjectKeyPreferWebp(stripped);
  // Replaces trailing .webp — safe for R2 object keys; revisit if query params are ever added.
  const withSmall = normalized.replace(/\.webp$/, '-150.webp');
  return `${ASSETS_BASE_URL}/${withSmall}`;
};

/** Pass as onError to any Image using getSmallAssetUrl — logs missing -150 variants so you know what to upload. */
export const onSmallImageError = (uri: string) => (): void => {
  logger.error(`[CDN] Missing -150 variant: ${uri}`);
};

/**
 * CDN folder name per avatar ID (assets/Avatars/{folder}/...).
 * Partial: complete-look girls (girl4-6) have no mood-folder structure — they render
 * from a single illustration wired directly in the avatar registry, not from here.
 */
const AVATAR_CDN_FOLDERS: Partial<Record<AvatarId, string>> = {
  [AVATAR_IDS.BLUE_WITH_HAIR]: 'BlueWithHair',
  [AVATAR_IDS.MOYKAN_BALL]: 'MoykanBall',
  [AVATAR_IDS.BLUE_WITH_CAP]: 'BlueWithCap',
  [AVATAR_IDS.GREEN_WHITE]: 'GreenWhite',
  [AVATAR_IDS.LIGHT_BLUE]: 'LightBlue',
  [AVATAR_IDS.LIGHT_RED]: 'LightRed',
  [AVATAR_IDS.WHITE_RED]: 'WhiteRed',
  [AVATAR_IDS.WHITE_YELLOW]: 'WhiteYellow',
  [AVATAR_IDS.MAC]: 'MAC',
};

const AVATAR_FILES = ['idle', 'happy', 'sad', 'home', 'away'] as const;

type AvatarPathEntry = Record<(typeof AVATAR_FILES)[number], string> & { angry: string };

const buildAvatarPaths = (folder: string): AvatarPathEntry => ({
  ...AVATAR_FILES.reduce(
    (acc, file) => {
      acc[file] = `assets/Avatars/${folder}/${file}.webp`;
      return acc;
    },
    {} as Record<(typeof AVATAR_FILES)[number], string>
  ),
  angry: `assets/Avatars/${folder}/angry.webp`,
});

// Only avatars with a CDN mood-folder get path entries; girl4-6 are wired in the registry.
const avatarEntries = (Object.entries(AVATAR_CDN_FOLDERS) as [AvatarId, string][]).map(
  ([id, folder]) => [id, buildAvatarPaths(folder)]
);

/** Monkey: reserved; uses Regular.webp, Win.webp, Lost.webp. No home/away on CDN; use idle (Regular.webp) as fallback. */
const MONKEY_AVATAR_PATHS = {
  idle: 'assets/Avatars/Monkey/Regular.webp',
  happy: 'assets/Avatars/Monkey/Win.webp',
  sad: 'assets/Avatars/Monkey/Lost.webp',
  angry: 'assets/Avatars/Monkey/Lost.webp',
} as const;

/** R2 object keys for known assets (for reuse and refactor safety) */
export const ASSET_PATHS = {
  /** Zporti mark (web favicon, etc.). CDN: https://assets.zporti.com/assets/logo.webp */
  LOGO_WEBP: 'assets/logo.webp',

  // Mascots (create flow, friend stats)
  MASCOT_YOU: 'assets/mascot-you.webp',
  MASCOT_FRIEND: 'assets/extracted_friend.webp',
  LIGHTNING_FIRE: 'assets/lightning-fire.webp',

  // Challenge-created transition backgrounds (PNG-only hero art)
  CHALLENGE_TRANSITION_BACKGROUND_CPU: 'assets/backgrounds/background-cpu.png',
  CHALLENGE_TRANSITION_BACKGROUND_PHONE: 'assets/backgrounds/background-phone.png',

  /** Redeem-coupon modal hero (gift + ticket art). CDN: assets/cupon_image.webp */
  COUPON_REDEEM_HERO: 'assets/cupon_image.webp',

  /** Coin store recharge banner (CDN). */
  COIN_STORE_BANNER: 'assets/backgrounds/coin_top%20(1).webp',

  /**
   * Animated mascot parts (PNG with alpha; layered rig — see components/onboarding/AnimatedMonkey).
   * Loaded bundle-first (assets/AnimatedMonkey/*) with these CDN keys as the fallback
   * (via getCdnUrlExactKey — PNG, no .webp rewrite).
   */
  ANIMATED_MONKEY: {
    BODY: 'assets/AnimatedMonkey/body.png',
    ARM: 'assets/AnimatedMonkey/arm.png',
    HEAD: 'assets/AnimatedMonkey/head.png',
    LID_L: 'assets/AnimatedMonkey/lidL.png',
    LID_R: 'assets/AnimatedMonkey/lidR.png',
  },

  // Coin images (CDN: assets/Coins/). No `-150` variants — use getAssetUrl, not getSmallAssetUrl.
  COINS: {
    COIN_IDLE: 'assets/Coins/coin.webp',
    COIN_20:  'assets/Coins/coin1.webp',
    COIN_100: 'assets/Coins/coins%203.webp',
    COIN_150: 'assets/Coins/coins%205.webp',
    COIN_BURST: 'assets/Coins/coin_burst.png',
  },

  // Layered avatar builder assets (CDN)
  AVATAR_LAYERS: {
    LAYERED_AVATAR: {
      BASE: 'assets/Avatar%20layers/layered%20avatar/avatar.webp',
      HATS: {
        HAT_1: 'assets/Avatar%20layers/layered%20avatar/hats/hat1.webp',
      },
      /** 150×150 kit preview thumbs in the layered-avatar folder (CDN). */
      KIT_PREVIEW_150: {
        BLACK_WHITE: 'assets/Avatar%20layers/layered%20avatar/hats/black-white-150.webp',
        CLARET_BLUE: 'assets/Avatar%20layers/layered%20avatar/hats/claret-blue-150.webp',
        GREEN_WHITE: 'assets/Avatar%20layers/layered%20avatar/hats/green-white-150.webp',
      },
    },
  },

  /**
   * Jersey illustration SVG (896×1200). Ship to R2 when used in release builds;
   * until then Metro can resolve the local file for dev.
   */
  JERSEY_ILLUSTRATION: 'assets/jersey-illustration.svg',

  /** Exact-hit / bullseye stat (World Cup arena leaderboard, picks). CDN: assets/target.webp */
  TARGET: 'assets/target.webp',

  /** Landing features panel image (Cloudflare upload). */
  LANDING_FEATURE_1: 'assets/feature1.png',
  /** Landing features panel image (Cloudflare upload). */
  LANDING_FEATURE_3: 'assets/feature3.png',

  // Landing page art — web-only marketing surfaces. PNG/JPG, use getCdnUrlExactKey.
  LANDING_HERO_MASCOT: 'assets/Landing/hero-mascot-square.png',
  LANDING_LOGO: 'assets/Landing/zporti-logo.jpg',
  LANDING_APP_FRIENDS: 'assets/Landing/app-friends.png',
  LANDING_APP_FIXTURES: 'assets/Landing/app-fixtures.png',
  LANDING_APP_H2H: 'assets/Landing/app-h2h.png',

  // Game result stat icons (CDN)
  TARGET_ICON: 'assets/target.webp',
  REWARD_ICON: 'assets/reward.webp',

  /**
   * Short SFX for gameplay feedback (CDN: assets/Sounds/*.mp3). Upload to R2; no webp rewrite for .mp3.
   * `UI_TAP_STAKE`: bundled in the app repo (`assets/Sounds/ui-tap-stake.mp3`) for local/native + web HTMLAudio; replace that file for a different coin clip. Set `EXPO_PUBLIC_SOUND_STAKE_FROM_CDN=true` to load this key from CDN only (R2 must have the file).
   * `UI_SECTION_EXPAND` / `UI_SECTION_COLLAPSE`: bundled repo MP3s (`ui-section-expand.mp3`, `ui-section-collapse.mp3`); upload both to R2 for CDN parity with other SFX keys.
   * `UI_MENU_OPEN_SWISH`: bundled `ui-menu-open-swish.mp3` for global menu drawer open; upload to R2 for CDN parity.
   */
  SOUNDS: {
    DAILY_CLAIM: 'assets/Sounds/daily-claim.mp3',
    CHALLENGE_WIN: 'assets/Sounds/challenge-win.mp3',
    CHALLENGE_LOST: 'assets/Sounds/challenge-lost.mp3',
    STREAK_MILESTONE: 'assets/Sounds/streak-milestone.mp3',
    UI_SUCCESS: 'assets/Sounds/ui-success.mp3',
    UI_DANGER_COMMIT: 'assets/Sounds/ui-danger-commit.mp3',
    UI_ERROR: 'assets/Sounds/ui-error.mp3',
    UI_WARNING: 'assets/Sounds/ui-warning.mp3',
    UI_INFO: 'assets/Sounds/ui-info.mp3',
    UI_DISMISS: 'assets/Sounds/ui-dismiss.mp3',
    UI_TAP: 'assets/Sounds/ui-tap.mp3',
    UI_TAP_NAV: 'assets/Sounds/ui-tap.mp3',
    UI_TAP_PICK: 'assets/Sounds/ui-tap.mp3',
    UI_TAP_STAKE: 'assets/Sounds/ui-tap-stake.mp3',
    UI_TAP_SOCIAL: 'assets/Sounds/ui-tap.mp3',
    UI_SECTION_EXPAND: 'assets/Sounds/ui-section-expand.mp3',
    UI_SECTION_COLLAPSE: 'assets/Sounds/ui-section-collapse.mp3',
    UI_MENU_OPEN_SWISH: 'assets/Sounds/ui-menu-open-swish.mp3',
    UI_PAPER_TEAR: 'assets/Sounds/ui-paper-tear.mp3',
    LEVEL_UP: 'assets/Sounds/level-up.mp3',
    RANK_UP: 'assets/Sounds/rank-up.mp3',
  },

  /** Rank badges for the XP system (specs/xp-and-ranks), 512px webp. Rank 1 (Rookie) → 10 (Icon). */
  BADGES: {
    RANK_01_ROOKIE: 'assets/Badges/rank-01-rookie.webp',
    RANK_02_PROSPECT: 'assets/Badges/rank-02-prospect.webp',
    RANK_03_CONTENDER: 'assets/Badges/rank-03-contender.webp',
    RANK_04_CHALLENGER: 'assets/Badges/rank-04-challenger.webp',
    RANK_05_VETERAN: 'assets/Badges/rank-05-veteran.webp',
    RANK_06_ALL_STAR: 'assets/Badges/rank-06-all-star.webp',
    RANK_07_CHAMPION: 'assets/Badges/rank-07-champion.webp',
    RANK_08_SUPERSTAR: 'assets/Badges/rank-08-superstar.webp',
    RANK_09_LEGEND: 'assets/Badges/rank-09-legend.webp',
    RANK_10_GOAT: 'assets/Badges/rank-10-goat.webp',
  },

  /**
   * Sport icons for the multi-sport challenge flow (specs/multi-sport-challenges).
   * Keyed by sport key so a new sport is one more line here plus one upload.
   * Source files live at assets/SportIcons/ and are uploaded by
   * `server/scripts/uploadSportIcons.mjs` — they are NOT bundled (assets-cdn.mdc).
   * Icons: Flaticon, free licence with attribution (see the legal Icon attribution section).
   *
   * Keys are VERSIONED (`-v1`). Objects are uploaded with a one-year immutable cache header, so an
   * icon can never be replaced in place — the edge would serve the old file for a year. Changing an
   * icon means bumping to `-v2` here and re-running the upload. The suffix also sidesteps
   * Cloudflare's negative caching, which holds a 404 for 4 hours after any pre-upload request.
   */
  SPORT_ICONS: {
    FOOTBALL: 'assets/icons/sports/football-v1.webp',
    BASKETBALL: 'assets/icons/sports/basketball-v1.webp',
    FORMULA_ONE: 'assets/icons/sports/formula-one-v1.webp',
  },

  /** Premium market crown badge (CDN: assets/icons/crown.png; PNG-only, use getCdnUrlExactKey). */
  CROWN_ICON: 'assets/icons/crown.png',

  /** Default group card / challenge cover when no custom image is set. */
  DEFAULT_GROUP_IMAGE: 'assets/Avatars/group.webp',

  /** Sign-in screen monkey mascot */
  SIGN_IN_MONKEY: 'assets/Avatars/Monkey/monkey-sign-in1.webp',

  // Avatar mood images (CDN: folder per AVATAR_CDN_FOLDERS; files = idle.webp, happy.webp, sad.webp, home.webp, away.webp).
  AVATARS: {
    ...Object.fromEntries(avatarEntries),
    monkey: MONKEY_AVATAR_PATHS,
  },
} as const;

// Load from CDN via ASSET_PATHS, not a bundled require(): bundled image requires do not
// resolve in the iOS production build (EAS_BUILD_DISABLE_BUNDLE_JAVASCRIPT_STEP=1), which is
// why the coin rendered nowhere on TestFlight. Same fix as the coin-store banner (33698c7e).
// See .cursor/rules/assets-cdn.mdc — this was a rule violation, not a one-off bug.
export const IDLE_COIN_SOURCE: ImageSourcePropType = {
  uri: getAssetUrl(ASSET_PATHS.COINS.COIN_IDLE),
};

/**
 * Default group cover when a group has no custom image.
 * Web: bundled require (Metro/webpack serves it reliably).
 * Native: CDN — iOS release builds often fail to paint bundled group.webp in expo-image.
 */
export const DEFAULT_GROUP_IMAGE_SOURCE: ImageSourcePropType =
  Platform.OS === 'web'
    ? require('../assets/Avatars/group.webp')
    : { uri: getAssetUrl(ASSET_PATHS.DEFAULT_GROUP_IMAGE) };
